import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, CheckCircle, Warning, Info, CircleNotch, X } from '@phosphor-icons/react';
import { ActionIcon, Badge, Text } from '@mantine/core';
import { useNotifications } from '../hooks/useNotifications';

function NotificationIcon({ type }) {
  switch (type) {
    case 'success':
      return <CheckCircle size={14} weight="fill" className="text-green-500 flex-shrink-0" />;
    case 'error':
      return <Warning size={14} weight="fill" className="text-red-500 flex-shrink-0" />;
    case 'warning':
      return <Warning size={14} weight="fill" className="text-yellow-500 flex-shrink-0" />;
    case 'loading':
      return <CircleNotch size={14} weight="bold" className="text-indigo-600 animate-spin flex-shrink-0" />;
    default:
      return <Info size={14} weight="fill" className="text-blue-500 flex-shrink-0" />;
  }
}

function formatTime(date, t) {
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 10) return t('shell.notifications.timeNow');
  if (diff < 60) return t('shell.notifications.timeSecondsAgo', { seconds: diff });
  if (diff < 3600) return t('shell.notifications.timeMinutesAgo', { minutes: Math.floor(diff / 60) });
  return date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
}

export default function TaskNotifications() {
  const { t } = useTranslation();
  const { notifications, removeNotification } = useNotifications();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const activeCount = notifications.filter(n => n.type === 'loading').length;
  const totalCount = notifications.length;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <ActionIcon
        variant="default"
        size="lg"
        radius="md"
        onClick={() => setOpen(!open)}
        aria-label={t('shell.notifications.ariaLabel')}
        styles={{
          root: {
            position: 'relative',
          },
        }}
      >
        <Bell size={16} weight={totalCount > 0 ? 'fill' : 'regular'} className="text-gray-500" />
        {totalCount > 0 && (
          <Badge
            size="xs"
            circle
            color="brand"
            className="absolute -top-1.5 -right-1.5"
            styles={{ root: { padding: 0, minWidth: 16, height: 16, fontSize: 9, fontWeight: 600 } }}
          >
            {totalCount > 9 ? '9+' : totalCount}
          </Badge>
        )}
        {activeCount > 0 && (
          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-[#1D4ED8] rounded-full animate-pulse" />
        )}
      </ActionIcon>

      {open && (
        <div className="absolute top-full right-0 mt-1.5 w-72 max-h-80 overflow-y-auto bg-white rounded-lg border border-gray-200 shadow-lg">
          <div className="px-3 py-2.5 border-b border-gray-200 flex items-center justify-between">
            <Text size="xs" fw={600} c="dark">{t('shell.notifications.title')}</Text>
            {totalCount > 0 && (
              <Badge size="xs" variant="light" color="gray">{totalCount}</Badge>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="p-6 text-center">
              <Bell size={24} weight="thin" className="text-gray-300 mx-auto mb-2" />
              <Text size="xs" c="dimmed">{t('shell.notifications.empty')}</Text>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map(notif => (
                <div key={notif.id} className="px-3 py-2.5 flex items-start gap-2.5 hover:bg-gray-50 transition">
                  <div className="mt-0.5">
                    <NotificationIcon type={notif.type} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Text size="xs" c="dark" className="leading-tight">{notif.message}</Text>
                    <Text size="xs" c="dimmed" mt={2}>{formatTime(notif.timestamp, t)}</Text>
                  </div>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="xs"
                    onClick={() => removeNotification(notif.id)}
                    aria-label={t('shell.notifications.remove')}
                  >
                    <X size={12} />
                  </ActionIcon>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
